import React, { useEffect, useState, useRef } from 'react';
import './styles.css';
import ZRMLogo from '../src/ZRM_logo.png';

export default function Report() {
  const popupRef = useRef(null);
  const sectionsRef = useRef(null);
  const [results, setResults] = useState({
    Health: {
      score_active: 0,
      score_inactive: 0,
      totalHealth: 0,
    },
  });
  const [showResults, setShowResults] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE;
  const ZOHO_REDIRECT_URI = 'https://healthchecker.zrm.dk/oauth/callback';
  const scope = process.env.REACT_APP_ZOHO_SCOPE;
  const client_id = process.env.REACT_APP_CLIENT_ID;
  const [showApiNames, setShowApiNames] = useState(false);
  const [visibleDuplicates, setVisibleDuplicates] = useState({});
  const [authenticated, setAuthenticated] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  let resultsPolling = false;
  let resultsCompleted = false;
  let interval;

  /** Check if session has accessToken **/
  const checkAuth = async () => {
    const res = await fetch(API_BASE+'/auth/status', { credentials: 'include' });
    if (!res.ok) {
      return startOAuth();
    }
    const js = await res.json();
    return js.authenticated;
  };

const startOAuth = () => {
  const state = "report";
  const redirectUri = ZOHO_REDIRECT_URI;

  popupRef.current = window.open(
    `https://accounts.zoho.eu/oauth/v2/auth?response_type=code&client_id=${client_id}&scope=${encodeURIComponent(scope)}&redirect_uri=${encodeURIComponent(redirectUri)}&access_type=offline&prompt=consent&state=${encodeURIComponent(state)}`,
    "ZohoOAuth",
    "width=600,height=700"
  );


  return new Promise((resolve, reject) => {
    if (!popupRef.current) {
      reject(new Error("Popup blocked"));
      return;
    }

    const cleanup = () => {
      clearInterval(timer);
      window.removeEventListener("message", handleMessage);
    };

    const timer = setInterval(() => {
      if (popupRef.current.closed) {
        cleanup();
        resolve(false); 
      }
    }, 500);

    const handleMessage = async (event) => {
      const allowedOrigins = [
        window.location.origin,
        'https://healthchecker.zrm.dk',
        'https://healthchecker-30041452713.development.catalystappsail.eu',
        'http://localhost:5000'
      ];
      if (!allowedOrigins.includes(event.origin)) return;

      let data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch(e) {
        return;
      }

      if (data?.type === "zoho-auth-success") {
        setAuthenticated(true);
        cleanup();
        resolve(true);
      }
    };

    window.addEventListener("message", handleMessage);
  });
};

 const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const fetchResults = async () => {
  // Prevent duplicate polling loops
  if (resultsCompleted) return; 
  if (resultsPolling) return;
  resultsPolling = true;

  if (!(await checkAuth())) {
    resultsPolling = false;
    console.log("Not logged in");
    return;
  }

  const res = await fetch(API_BASE+'/results', {
    credentials: 'include'
  });
  console.log("res",res);

  if (res.status === 401) {
    resultsPolling = false;
    return;
  }

  if (res.status === 202) {
    console.log("Results still loading, retrying...");
    return;
  }

  const data = await res.json();

  if (data.done) {
    resultsCompleted = true; 
    resultsPolling = false;
    setResults(data.output);
    setShowResults(true);
    resultsPolling = false;
    return; 
  }
  resultsPolling = false;

  setTimeout(fetchResults, 30000);
};

useEffect(() => {
    const init = async () => {
      const authed = await startOAuth(); // We want to always log in
      if (!authed) {
        return;
      }
      fetchResults();
    };

    init();
    return () => clearInterval(interval);
  }, []); 


const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return Number(value).toLocaleString('en-US');
};

const duplicatesByModule  = results?.analysis?.duplicatesByModule;
const totalDuplicates     = results?.analysis?.totalDuplicates;
const noActivityByModule  = results?.analysis?.noActivityByModule;
const totalNoActivity     = results?.analysis?.totalNoActivity;
const emptyFieldsByModule = results?.analysis?.emptyFieldsByModule;
const totalEmptyFields    = results?.analysis?.totalEmptyFields;
const duplicatesImpact    = results?.analysis?.duplicatesImpact;
const overdueCount        = results?.analysis?.overdueCount;
const overdueImpact       = results?.analysis?.overdueImpact;
const noActivityImpact    = results?.analysis?.noActivityImpact;
const emptyImpact         = results?.analysis?.emptyImpact;
const activeImpact        = results?.analysis?.activeImpact;
const inactiveImpact      = results?.analysis?.inactiveImpact;

const today               = formatDate(results?.today);
const moduleLabels        = results?.moduleLabels;
const sortByModuleAndName = (a, b) => {
  const moduleA = moduleLabels[a.module?.api_name] || a.module?.api_name || '';
  const moduleB = moduleLabels[b.module?.api_name] || b.module?.api_name || '';
  if (moduleA !== moduleB) {
    return moduleA.localeCompare(moduleB);
  }
  return a.name.localeCompare(b.name);
};
const activeWorkflows = [...(results?.activeWorkflows || [])]
  .sort(sortByModuleAndName);
const activeWorkFlowsPerModule = (results?.activeWorkflowsPerModule || 0);
const inactiveWorkflows = [...(results?.inactiveWorkflows || [])]
  .sort(sortByModuleAndName);
const count_inactiveWorkflows = (results?.analysis?.count_inactiveWorkflows || 0);

const getVisible = (moduleName, type) =>
  visibleDuplicates?.[moduleName]?.[type] ?? 50;

const showMore = (moduleName, type, step = 50) =>
  setVisibleDuplicates((prev) => ({
    ...prev,
    [moduleName]: {
      ...(prev[moduleName] || {}),
      [type]: (prev[moduleName]?.[type] ?? 50) + step,
    },
  }));

const sendToBookingPage = async () => {
  window.open("https://zrm.dk/book/", "_blank");
}

const toggleExpandAll = () => {
  const next = !allExpanded;
  sectionsRef.current?.querySelectorAll('details').forEach((d) => {
    d.open = next;
  });
  setAllExpanded(next);
};

const rawHealth = results?.analysis?.rawHealth || 0;
const totalHealth = Number(rawHealth);

let healthClass = 'health-unknown';
if (totalHealth <= 60) healthClass = 'health-bad';
else if (totalHealth >= 80) healthClass = 'health-good';
else healthClass = 'health-warning';

const sections = [
  {
    key: 'duplicates',
    impact: duplicatesImpact,
    show: totalDuplicates > 0,
    render: () => (
        <details className="info-box">
          <summary className="summary-row">
            <h3 className="summary-text">
              The system has {formatNumber(totalDuplicates)} duplicate value
              {totalDuplicates === 1 ? '' : 's'}
            </h3>
            <span className="impact-box">{duplicatesImpact}</span>
          </summary>

          <div className="duplicates-modules">
            {duplicatesByModule.map((m) => (
              <details key={m.moduleName} className="info-box">
                <summary className="summary-row">
                  <span>
                    {m.label}
                    {showApiNames ? ` (${m.moduleName}) ` : ' '} 
                    has <strong>{formatNumber(m.total)}</strong> duplicate
                    {m.total === 1 ? '' : 's'}
                  </span>
                </summary>

                <div className="duplicates-types">
                  {(m.apiDuplicates?.length ?? 0) > 0 && (
                    <details>
                      <summary className="duplicate-type-summary">
                        There {m.apiDuplicates.length === 1 ? 'is' : 'are'}{' '}
                        {m.apiDuplicates.length} duplicate{' '}
                        {m.moduleName === 'Accounts' ? 'account name' : 'email'}
                        {m.apiDuplicates.length === 1 ? '' : 's'}
                      </summary>

                      <ul className="duplicates-grid">
                        {m.apiDuplicates
                          .slice(0, getVisible(m.moduleName, 'api'))
                          .map((dup, idx) => (
                            <li key={idx}>
                              <a href={`https://crm.zoho.eu/crm/GlobalSearch1.do?sModules=${encodeURIComponent(m.moduleName)}&searchword=${encodeURIComponent(dup)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="duplicate-link"
                                onClick={(e) => e.stopPropagation()}>
                                {dup}
                              </a>
                            </li>
                          ))}
                      </ul>

                      {getVisible(m.moduleName, 'api') < m.apiDuplicates.length && (
                        <button
                          type="button"
                          className="button-primary"
                          onClick={() => showMore(m.moduleName, 'api')}
                        >
                          Show more
                        </button>
                      )}
                    </details>
                  )}

                  {(m.phoneDuplicates?.length ?? 0) > 0 && (
                    <details>
                      <summary className="duplicate-type-summary">
                        There {m.phoneDuplicates.length === 1 ? 'is' : 'are'}{' '}
                        {m.phoneDuplicates.length} duplicate phone
                        {m.phoneDuplicates.length === 1 ? '' : 's'}
                      </summary>

                      <ul className="duplicates-grid">
                        {m.phoneDuplicates
                          .slice(0, getVisible(m.moduleName, 'phones'))
                          .map((dup, idx) => (
                            <li key={idx}>
                              <a href={`https://crm.zoho.eu/crm/GlobalSearch1.do?sModules=${encodeURIComponent(m.moduleName)}&searchword=${encodeURIComponent(dup)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="duplicate-link"
                                onClick={(e) => e.stopPropagation()}>
                                {dup}
                              </a>
                            </li>
                          ))}
                      </ul>

                      {getVisible(m.moduleName, 'phones') < m.phoneDuplicates.length && (
                        <button
                          type="button"
                          className="button-primary"
                          onClick={() => showMore(m.moduleName, 'phones')}
                        >
                          Show more
                        </button>
                      )}
                    </details>
                  )}
                </div>
              </details>
            ))}
          </div>
      </details>
    )
  },
  {
    key: 'tasks',
    impact: overdueImpact,
    show: (overdueCount ?? 0) > 0,
    render: () => (
      <details className="info-box no-more-info-box">
        <summary className="summary-row">
          <h3 className="summary-text">
            The system has {formatNumber(overdueCount)} overdue task
            {overdueCount === 1 ? '' : 's'}
          </h3>
          <span className="impact-box">{overdueImpact}</span>
        </summary>
      </details>
    )
  },
  {
    key: 'noActivity',
    impact: noActivityImpact,
    show: totalNoActivity > 0,
    render: () => (
      <details className="info-box">
        <summary className="summary-row">
          <h3 className="summary-text">
            The system has {formatNumber(totalNoActivity)} record
            {totalNoActivity === 1 ? '' : 's'} without activity
          </h3>
          <span className="impact-box">{noActivityImpact}</span>
        </summary>

        <div className="duplicates-modules">
          {noActivityByModule.map((m) => (
                <div className="summary-row">
                  {m.label}
                  {showApiNames ? ` (${m.moduleName})` : ''} has{' '}
                  <strong>{formatNumber(m.count)}</strong> record
                  {m.count === 1 ? '' : 's'} without activity in the past
                  {m.moduleName === 'Leads' ? ' one month' : ' three months'}
                </div>
          ))}
        </div>
      </details>
    )
  },

  {
    key: 'empty',
    impact: emptyImpact,
    show: totalEmptyFields > 0,
    render: () => (
      <details className="info-box">
        <summary className="summary-row">
          <h3 className="summary-text">
            The system has {formatNumber(totalEmptyFields)} empty field
            {totalEmptyFields === 1 ? '' : 's'}
          </h3>
          <span className="impact-box">{emptyImpact}</span>
        </summary>
          <div>
          {emptyFieldsByModule.map((m) => (
            <details key={m.moduleName} className="info-box">
              <summary>
                <div className="summary-row">
                  <span>
                    {m.label}
                    {showApiNames ? ` (${m.moduleName})` : ''}
                    {' '}has <strong>{formatNumber(m.count)}</strong> empty field
                    {m.count === 1 ? '' : 's'}
                  </span>
                </div>
              </summary>
              <div className="percent_div duplicates-grid">
                {m.fields.length > 0 &&
                [...m.fields]
                  .sort((a, b) => a.label.localeCompare(b.label, 'da'))
                  .map((f) => (
                    <div key={f.key} className="field_div">
                      <div className="field-name_div">
                        <span>{f.label}</span>
                        {showApiNames ? (
                          <span className="api-text"> ({f.api})</span>
                        ) : null}
                      </div>
                      <div className="field-percent_div">{f.emptyPercent}%</div>
                    </div>
                  ))}
              </div>
            </details>
          ))}
          </div>
      </details>
    )
  },

  {
    key: 'activeWorkflows',
    impact: activeImpact,
    show: (activeWorkFlowsPerModule < 5),
    render: () => (
      <details className="info-box">
        <summary className="summary-row">
          <h3 className="summary-text">
            The system only has {formatNumber(activeWorkFlowsPerModule)} active workflow
            {activeWorkFlowsPerModule === 1 ? ' ' : 's '}
            per module
          </h3>
          <span className="impact-box">{activeImpact}</span>
        </summary>
          <div>
              <div className="workflow_div">
              <table className="min-w-full text-sm text-left border border-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                        <th>Name</th>
                        <th>Module</th>
                        {showApiNames && (
                          <th>API</th>
                        )}
                        <th>Created By</th>
                        <th>Last Modified</th>
                    </tr>
                    </thead>
                    <tbody>
                    {(activeWorkflows ?? []).map((wf) => (
                        <tr key={wf.id} className="hover:bg-gray-50">
                        <td>{wf.name || '—'}</td>
                        <td>{moduleLabels?.[wf.module?.api_name] || wf.module?.api_name || '—'}</td>
                        {showApiNames && (
                        <td>{wf.module?.api_name || '—'}</td>
                        )}
                        <td>{wf.created_by?.name || '—'}</td>
                        <td>{wf.modified_time ? formatDate(wf.modified_time) : '—'}</td>
                        </tr>
                    ))}
                    </tbody>
              </table>
              </div>
          </div>
        </details>
    )
  },

  {
    key: 'inactiveWorkflows',
    impact: inactiveImpact,
    show: (count_inactiveWorkflows > 0),
    render: () => (
      <details className="info-box">
        <summary className="summary-row">
          <h3 className="summary-text">
            The system has {count_inactiveWorkflows} inactive workflow
            {inactiveWorkflows?.length === 1 ? '' : 's'}
          </h3>
          <span className="impact-box">{inactiveImpact}</span>
        </summary>
        <div>
            <div>
                <div className="workflow_div">
                <table className="min-w-full text-sm text-left border border-gray-200">
                    <thead className="bg-gray-100">
                    <tr>
                        <th>Name</th>
                        <th>Module</th>
                        {showApiNames && (
                          <th>API</th>
                        )}
                        <th>Created By</th>
                        <th>Last Modified</th>
                    </tr>
                    </thead>
                    <tbody>
                    {(inactiveWorkflows ?? []).map((wf) => (
                        <tr key={wf.id} className="hover:bg-gray-50">
                        <td>{wf.name || '—'}</td>
                        <td>{moduleLabels?.[wf.module?.api_name] || wf.module?.api_name || '—'}</td>
                        {showApiNames && (
                        <td>{wf.module?.api_name || '—'}</td>
                        )}
                        <td>{wf.created_by?.name || '—'}</td>
                        <td>{wf.modified_time ? formatDate(wf.modified_time) : '—'}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
        </details>
    )
  }
];

  return (
    <div className="landing">
                <a href="https://zrm.dk">
      <img fetchpriority="high" width="100" src="https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1.png" className="attachment-full size-full wp-image-5631" alt="ZRM logo mørk" srcSet="https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1.png 6329w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-300x89.png 300w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-1024x305.png 1024w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-768x229.png 768w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-1536x457.png 1536w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-2048x610.png 2048w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-1568x467.png 1568w" sizes="(max-width: 6329px) 100vw, 6329px" />
        </a>
          <div className="topBanner">
            <h1> Health Checker</h1>
          </div>
      <div className="report-header">
        {(!authenticated) && (
          <h2>Awaiting log in. Allow popups and reload to view the report</h2>
        )}
        {(authenticated && (!showResults || !results)) && (
          <h2>Report is still generating. You will receive an email when it is done</h2>
        )}
        
      </div>
      <div>
        <div>
        {showResults && results && (
          <div ref={sectionsRef}>
            <div className={`health-summary ${healthClass}`}>
              <div className="health-label">Health Score</div>
              <div className="health-value">
                {typeof totalHealth === 'number' ? `${totalHealth.toFixed(1)}%` : '—'}
              </div>
            </div>
          {(healthClass === 'health-bad') && (
            <div className='booking-div'>
              <button
                className="cta-button"
                onClick={sendToBookingPage}>
                Book free it-consultation
              </button>
            </div>
          )}
          {authenticated && showResults && results && (
            <div className="report-header-card">
              <div className="report-header-meta-report-side">
                <span className="report-date-label">Report generated</span>
                <span className="report-date-value">{today}</span>
              </div>
            </div>
          )}
          {results?.analysis && (
            <div className="analysis-container">
              <h3>{results?.analysis?.introduction}</h3>
              
              <ul>
                {results?.analysis?.points.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>

              <p>{results?.analysis?.ending}</p>
            </div>
          )}
            <button
                type="button"
                className="button-primary"
                onClick={() => setShowApiNames((v) => !v)}
                >
                {showApiNames ? 'Hide API names' : 'Show API names'}
            </button>
            <button
                type="button"
                className="button-primary"
                onClick={toggleExpandAll}>
                {allExpanded ? 'Close All' : 'Expand All'}
            </button>
            {sections
              .filter((s) => s.show)
              .sort((a, b) => {
                // Håndterer impact som både procentstreng og tal
                const parseImpact = (impact) => {
                  if (typeof impact === 'string') {
                    const num = parseFloat(impact.replace('%', ''));
                    return isNaN(num) ? 0 : num; // Hvis værdien ikke er et tal, returner 0
                  }
                  return impact; // Hvis det allerede er et tal, returner det direkte
                };

                const aImpact = parseImpact(a.impact);
                const bImpact = parseImpact(b.impact);

                return aImpact - bImpact; // Sorter mest negativ først
              })
              .map((s) => (
                <React.Fragment key={s.key}>
                  {s.render()}
                </React.Fragment>
              ))}

        </div>
        )}
      </div>
    </div>
    {/* Fixed Footer */}
    <div className="footer">
      <p>Powered by </p>
      <img
        src={ZRMLogo}
        alt="Powered by ZRM"
        style={{
          maxWidth: "50px",
          height: "auto",
          marginRight: "10px",
          verticalAlign: "middle",
        }}
      />
    </div>
      </div>
  );
}
